import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AuthService from "../services/auth.service";
import TopBar from "../components/TopBar";

function Help() {
  const navigate = useNavigate();

  useEffect(() => {
    //Runs only on the first render
    const currentUser = AuthService.getCurrentUser();
    if (!currentUser) {
      navigate("/login");
      return;
    }
    // The menu entry is admin-only until the professors supply real content, but
    // the route stayed reachable by typing the URL. Placeholder agronomic advice
    // must not reach students, so gate the page itself the same way.
    if (!currentUser.roles.includes("ROLE_ADMIN")) {
      navigate("/");
    }
  }, [navigate]);
  return (
    <div className="dark:bg-slate-900">
      <TopBar link=""></TopBar>
      <div className="flex flex-col items-center">
        <div className="max-w-[1400px] md:bg-white md:pb-3 dark:md:bg-slate-900">
          <div className="mx-4">
            {/* Header */}
            <div className="mb-8 mt-6">
              <h1 className="mb-2 text-3xl font-bold text-stone-900 dark:text-slate-100">
                Guide éducatif - Élevage de veaux
              </h1>
              <p className="text-stone-600 dark:text-slate-300">
                Bienvenue dans l'application Check'veau ! Ce guide éducatif vous aidera à
                comprendre les concepts fondamentaux de l'élevage de veaux et la signification
                des différents paramètres que vous rencontrerez dans l'application.
              </p>
            </div>

            {/* Table of Contents */}
            <div className="mb-8 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-900/20">
              <h2 className="mb-3 text-lg font-semibold text-amber-800 dark:text-amber-300">
                Table des matières
              </h2>
                    <ul className="space-y-2 text-sm dark:text-slate-300">
                <li>
                  <a
                    href="#identification"
                    className="text-amber-700 underline hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-200"
                  >
                    1. Identification et traçabilité
                  </a>
                </li>
                <li>
                  <a
                    href="#birth-assessment"
                    className="text-amber-700 underline hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-200"
                  >
                    2. Évaluation à la naissance
                  </a>
                </li>
                <li>
                  <a
                    href="#colostrum"
                    className="text-amber-700 underline hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-200"
                  >
                    3. Le colostrum et son importance
                  </a>
                </li>
                <li>
                  <a
                    href="#growth-monitoring"
                    className="text-amber-700 underline hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-200"
                  >
                    4. Suivi de la croissance (GMQ)
                  </a>
                </li>
                <li>
                  <a
                    href="#milk-feeding"
                    className="text-amber-700 underline hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-200"
                  >
                    5. Alimentation lactée
                  </a>
                </li>
                <li>
                  <a
                    href="#health-management"
                    className="text-amber-700 underline hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-200"
                  >
                    6. Gestion sanitaire
                  </a>
                </li>
                <li>
                  <a
                    href="#procedures"
                    className="text-amber-700 underline hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-200"
                  >
                    7. Interventions zootechniques
                  </a>
                </li>
                <li>
                  <a
                    href="#supplements"
                    className="text-amber-700 underline hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-200"
                  >
                    8. Compléments nutritionnels
                  </a>
                </li>
              </ul>
            </div>

            {/* Identification Section */}
            <div id="identification" className="mb-12">
              <h2 className="mb-4 text-2xl font-bold text-stone-900 dark:text-slate-100">
                1. Identification et traçabilité
              </h2>
              <div className="prose prose-stone max-w-none dark:prose-invert">
                <h3 className="mb-3 text-xl font-semibold text-stone-800 dark:text-slate-200">
                  BDTA (Base de Données de Traçabilité des Animaux)
                </h3>
                <p className="mb-4 dark:text-slate-300">
                  Le BDTA est le système d'identification officiel des bovins en Suisse. 
                  Chaque animal reçoit un numéro unique qui permet de le suivre tout au long 
                  de sa vie, de la naissance à l'abattage.
                </p>
                
                <div className="mb-6 border-l-4 border-blue-400 bg-blue-50 p-4 dark:border-blue-500 dark:bg-blue-900/20">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-blue-400 dark:text-blue-300"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        <strong>Importance :</strong> Le BDTA garantit la traçabilité 
                        complète des animaux et est obligatoire pour tous les bovins 
                        en Suisse. Il permet de retracer l'origine des produits 
                        alimentaires et de gérer les crises sanitaires.
                      </p>
                    </div>
                  </div>
                </div>

                <h3 className="mb-3 text-xl font-semibold text-stone-800 dark:text-slate-200">
                  Éléments d'identification
                </h3>
                <ul className="mb-4 list-disc space-y-2 pl-6 dark:text-slate-300">
                  <li><strong>Numéro BDTA :</strong> Identifiant unique officiel</li>
                  <li><strong>Nom :</strong> Nom donné par l'éleveur pour faciliter la gestion</li>
                  <li><strong>Sexe :</strong> Mâle ou femelle (influence la gestion)</li>
                  <li><strong>Race :</strong> Type génétique (Holstein, Charolaise, etc.)</li>
                  <li><strong>Date et heure de naissance :</strong> Précision importante pour le suivi</li>
                </ul>

                <div className="mb-6 border-l-4 border-yellow-400 bg-yellow-50 p-4 dark:border-yellow-500 dark:bg-yellow-900/20">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-yellow-400 dark:text-yellow-300"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700 dark:text-yellow-300">
                        <strong>Conseil pratique :</strong> Utilisez des noms 
                        descriptifs qui incluent l'année de naissance et un 
                        numéro séquentiel (ex: "Belle-2024-001") pour faciliter 
                        l'organisation de votre troupeau.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Birth Assessment Section */}
            <div id="birth-assessment" className="mb-12">
              <h2 className="mb-4 text-2xl font-bold text-stone-900 dark:text-slate-100">
                2. Évaluation à la naissance
              </h2>
              <div className="prose prose-stone max-w-none dark:prose-invert">
                <h3 className="mb-3 text-xl font-semibold text-stone-800 dark:text-slate-200">
                  Vitalité du veau
                </h3>
                <p className="mb-4 dark:text-slate-300">
                  L'évaluation de la vitalité à la naissance est cruciale pour déterminer 
                  les soins immédiats nécessaires et prévoir le suivi du veau.
                </p>

                <div className="mb-6 grid gap-4 md:grid-cols-3">
                  <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-700 dark:bg-green-900/20">
                    <h4 className="mb-2 font-semibold text-green-800 dark:text-green-300">Vif</h4>
                    <ul className="space-y-1 text-sm text-green-700 dark:text-green-300">
                      <li>• Réflexe de succion présent</li>
                      <li>• Respiration normale</li>
                      <li>• Réactions aux stimuli</li>
                      <li>• Tente de se lever rapidement</li>
                    </ul>
                  </div>
                  <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-700 dark:bg-yellow-900/20">
                    <h4 className="mb-2 font-semibold text-yellow-800 dark:text-yellow-300">Apathique</h4>
                    <ul className="space-y-1 text-sm text-yellow-700 dark:text-yellow-300">
                      <li>• Réflexes faibles</li>
                      <li>• Respiration lente</li>
                      <li>• Réactions limitées</li>
                      <li>• Difficulté à se lever</li>
                    </ul>
                  </div>
                  <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-700 dark:bg-red-900/20">
                    <h4 className="mb-2 font-semibold text-red-800 dark:text-red-300">Mort</h4>
                    <ul className="space-y-1 text-sm text-red-700 dark:text-red-300">
                      <li>• Aucun réflexe</li>
                      <li>• Pas de respiration</li>
                      <li>• Pas de réaction</li>
                      <li>• Mortinatalité</li>
                    </ul>
                  </div>
                </div>

                <h3 className="mb-3 text-xl font-semibold text-stone-800 dark:text-slate-200">
                  Déroulement de la naissance
                </h3>
                <p className="mb-4 dark:text-slate-300">
                  Le type de naissance influence la santé future du veau et nécessite 
                  des soins adaptés.
                </p>

                <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-700 dark:bg-blue-900/20">
                  <h4 className="mb-2 font-semibold text-blue-800 dark:text-blue-300">
                    Types de naissance :
                  </h4>
                  <ul className="space-y-2 text-sm dark:text-slate-300">
                    <li>
                      <strong>Sans aide :</strong> Naissance naturelle, veau vigoureux, 
                      pas d'intervention nécessaire
                    </li>
                    <li>
                      <strong>Aide faible :</strong> Légère assistance (traction douce), 
                      veau généralement en bonne santé
                    </li>
                    <li>
                      <strong>Naissance difficile :</strong> Intervention importante, 
                      risque de complications, surveillance renforcée nécessaire
                    </li>
                  </ul>
                </div>

                <h3 className="mb-3 text-xl font-semibold text-stone-800 dark:text-slate-200">
                  Lieu de naissance
                </h3>
                <p className="mb-4 dark:text-slate-300">
                  L'environnement de naissance influence les risques sanitaires et 
                  la qualité des premiers soins.
                </p>

                <ul className="mb-4 list-disc space-y-2 pl-6 dark:text-slate-300">
                  <li><strong>Box de vêlage :</strong> Environnement contrôlé, hygiène optimale</li>
                  <li><strong>Prairie :</strong> Environnement naturel, surveillance plus difficile</li>
                  <li><strong>Au sein du troupeau :</strong> Contact avec d'autres animaux, risque de contamination</li>
                  <li><strong>Autre :</strong> Situations particulières nécessitant une attention spéciale</li>
                </ul>

                <div className="mb-6 border-l-4 border-yellow-400 bg-yellow-50 p-4 dark:border-yellow-500 dark:bg-yellow-900/20">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-yellow-400 dark:text-yellow-300"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700 dark:text-yellow-300">
                        <strong>Important :</strong> Un veau apathique ou né avec difficulté 
                        nécessite une surveillance rapprochée et des soins immédiats, 
                        notamment l'administration rapide de colostrum de qualité.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Colostrum Section */}
            <div id="colostrum" className="mb-12">
              <h2 className="mb-4 text-2xl font-bold text-stone-900 dark:text-slate-100">
                3. Le colostrum et son importance
              </h2>
              <div className="prose prose-stone max-w-none dark:prose-invert">
                <h3 className="mb-3 text-xl font-semibold text-stone-800 dark:text-slate-200">
                  Qu'est-ce que le colostrum ?
                </h3>
                <p className="mb-4 dark:text-slate-300">
                  Le colostrum est le premier lait produit par la vache après la mise bas. 
                  Il est essentiellement différent du lait normal et contient des concentrations 
                  élevées d'anticorps (immunoglobulines), de protéines, de vitamines et de 
                  minéraux nécessaires à la survie du veau.
                </p>

                <div className="mb-6 border-l-4 border-red-400 bg-red-50 p-4 dark:border-red-500 dark:bg-red-900/20">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-red-400 dark:text-red-300"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700 dark:text-red-300">
                        <strong>Critique :</strong> Le veau naît sans immunité et dépend 
                        entièrement du colostrum pour sa protection contre les maladies. 
                        Sans colostrum de qualité dans les premières heures, le veau 
                        risque de mourir ou de développer des infections graves.
                      </p>
                    </div>
                  </div>
                </div>

                <h3 className="mb-3 text-xl font-semibold text-stone-800 dark:text-slate-200">
                  Règles d'or du colostrum
                </h3>
                <div className="mb-6 grid gap-4 md:grid-cols-3">
                  <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-700 dark:bg-green-900/20">
                    <h4 className="mb-2 font-semibold text-green-800 dark:text-green-300">Rapidité</h4>
                    <p className="text-sm text-green-700 dark:text-green-300">
                      Dans les 2 premières heures de vie. L'absorption des anticorps 
                      diminue rapidement après 6 heures.
                    </p>
                  </div>
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-700 dark:bg-blue-900/20">
                    <h4 className="mb-2 font-semibold text-blue-800 dark:text-blue-300">Quantité</h4>
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      Minimum 4 litres dans les 12 premières heures, 
                      idéalement 2 litres dans les 2 premières heures.
                    </p>
                  </div>
                  <div className="rounded-lg border border-purple-200 bg-purple-50 p-4 dark:border-purple-700 dark:bg-purple-900/20">
                    <h4 className="mb-2 font-semibold text-purple-800 dark:text-purple-300">Qualité</h4>
                    <p className="text-sm text-purple-700 dark:text-purple-300">
                      Colostrum de première traite, de bonne qualité, 
                      mesuré avec un réfractomètre Brix.
                    </p>
                  </div>
                </div>

                <h3 className="mb-3 text-xl font-semibold text-stone-800 dark:text-slate-200">
                  Sources de colostrum
                </h3>
                <ul className="mb-4 list-disc space-y-2 pl-6 dark:text-slate-300">
                  <li>
                    <strong>Maternel :</strong> Colostrum de la propre mère du veau, 
                    idéal car adapté à l'environnement de l'élevage
                  </li>
                  <li>
                    <strong>Congelé :</strong> Colostrum de qualité stocké au congélateur, 
                    pratique pour les situations d'urgence
                  </li>
                  <li>
                    <strong>Autre :</strong> Colostrum d'autres vaches du troupeau, 
                    à utiliser en dernier recours
                  </li>
                </ul>

                <h3 className="mb-3 text-xl font-semibold text-stone-800 dark:text-slate-200">
                  Méthodes d'administration
                </h3>
                <div className="mb-6 grid gap-4 md:grid-cols-2">
                  <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-700 dark:bg-green-900/20">
                    <h4 className="mb-2 font-semibold text-green-800 dark:text-green-300">Biberon</h4>
                    <ul className="space-y-1 text-sm text-green-700 dark:text-green-300">
                      <li>• Méthode naturelle</li>
                      <li>• Stimule le réflexe de succion</li>
                      <li>• Moins de stress pour le veau</li>
                      <li>• Nécessite un veau vigoureux</li>
                    </ul>
                  </div>
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-700 dark:bg-blue-900/20">
                    <h4 className="mb-2 font-semibold text-blue-800 dark:text-blue-300">Sonde</h4>
                    <ul className="space-y-1 text-sm text-blue-700 dark:text-blue-300">
                      <li>• Garantit l'administration</li>
                      <li>• Pour veaux faibles</li>
                      <li>• Technique délicate</li>
                      <li>• Risque d'aspiration</li>
                    </ul>
                  </div>
                </div>

                <h3 className="mb-3 text-xl font-semibold text-stone-800 dark:text-slate-200">
                  Mesure de la qualité : Brix
                </h3>
                <p className="mb-4 dark:text-slate-300 dark:prose-invert">
                  Le réfractomètre Brix est l'outil standard pour mesurer la qualité 
                  du colostrum. Il mesure la concentration en solides totaux, qui 
                  est directement corrélée à la teneur en immunoglobulines.
                </p>

                <div className="mb-6 grid gap-4 md:grid-cols-3">
                  <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-700 dark:bg-green-900/20">
                    <h4 className="mb-2 font-semibold text-green-800 dark:text-green-300">Excellent</h4>
                    <p className="text-sm text-green-700 dark:text-green-300">
                      <strong>≥ 22% Brix</strong><br/>
                      Colostrum de très haute qualité, 
                      riche en anticorps
                    </p>
                  </div>
                  <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-700 dark:bg-yellow-900/20">
                    <h4 className="mb-2 font-semibold text-yellow-800 dark:text-yellow-300">Bon</h4>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300">
                      <strong>18-21% Brix</strong><br/>
                      Qualité acceptable, 
                      peut être utilisé
                    </p>
                  </div>
                  <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-700 dark:bg-red-900/20">
                    <h4 className="mb-2 font-semibold text-red-800 dark:text-red-300">Mauvais</h4>
                    <p className="text-sm text-red-700 dark:text-red-300">
                      <strong>&lt; 18% Brix</strong><br/>
                      Qualité insuffisante, 
                      ne pas utiliser
                    </p>
                  </div>
                </div>

                <div className="mb-6 border-l-4 border-yellow-400 bg-yellow-50 p-4 dark:border-yellow-500 dark:bg-yellow-900/20">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-yellow-400 dark:text-yellow-300"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700 dark:text-yellow-300">
                        <strong>Rappel important :</strong> La qualité du colostrum se 
                        dégrade rapidement après la première traite. Il est essentiel 
                        de mesurer sa qualité avec un réfractomètre Brix et de l'administrer 
                        rapidement pour maximiser l'immunité du veau.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Growth Monitoring Section */}
            <div id="growth-monitoring" className="mb-12">
              <h2 className="mb-4 text-2xl font-bold text-stone-900 dark:text-slate-100">
                4. Suivi de la croissance (GMQ)
              </h2>
              <div className="prose prose-stone max-w-none dark:prose-invert">
                <h3 className="mb-3 text-xl font-semibold text-stone-800 dark:text-slate-200">
                  Qu'est-ce que le GMQ ?
                </h3>
                <p className="mb-4 dark:text-slate-300">
                  Le GMQ (Gain Moyen Quotidien) est un indicateur clé de la performance 
                  de croissance d'un veau. Il représente le gain de poids moyen par jour 
                  sur une période donnée et permet d'évaluer l'efficacité de l'alimentation 
                  et la santé générale de l'animal.
                </p>

                <div className="mb-6 border-l-4 border-blue-400 bg-blue-50 p-4 dark:border-blue-500 dark:bg-blue-900/20">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-blue-400 dark:text-blue-300"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        <strong>Formule de calcul :</strong> GMQ = (Poids final - Poids initial) ÷ Nombre de jours
                        <br/>
                        <strong>Exemple :</strong> Un veau passe de 40 kg à 50 kg en 10 jours
                        <br/>
                        GMQ = (50 - 40) ÷ 10 = 1,0 kg/jour
                      </p>
                    </div>
                  </div>
                </div>

                <h3 className="mb-3 text-xl font-semibold text-stone-800 dark:text-slate-200">
                  Interprétation du GMQ
                </h3>
                <div className="mb-6 grid gap-4 md:grid-cols-3">
                  <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-700 dark:bg-green-900/20">
                    <h4 className="mb-2 font-semibold text-green-800 dark:text-green-300">Excellent</h4>
                    <p className="text-sm text-green-700 dark:text-green-300">
                      <strong>≥ 0,75 kg/jour</strong><br/>
                      Croissance optimale, 
                      alimentation efficace
                    </p>
                  </div>
                  <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-700 dark:bg-yellow-900/20">
                    <h4 className="mb-2 font-semibold text-yellow-800 dark:text-yellow-300">Acceptable</h4>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300">
                      <strong>0,5-0,74 kg/jour</strong><br/>
                      Croissance normale, 
                      surveillance recommandée
                    </p>
                  </div>
                  <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-700 dark:bg-red-900/20">
                    <h4 className="mb-2 font-semibold text-red-800 dark:text-red-300">Insuffisant</h4>
                    <p className="text-sm text-red-700 dark:text-red-300">
                      <strong>&lt; 0,5 kg/jour</strong><br/>
                      Problème de santé ou 
                      d'alimentation
                    </p>
                  </div>
                </div>

                <h3 className="mb-3 text-xl font-semibold text-stone-800 dark:text-slate-200">
                  Facteurs influençant le GMQ
                </h3>
                <div className="mb-6 grid gap-4 md:grid-cols-2">
                  <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-700 dark:bg-green-900/20">
                    <h4 className="mb-2 font-semibold text-green-800 dark:text-green-300">Facteurs positifs</h4>
                    <ul className="space-y-1 text-sm text-green-700 dark:text-green-300">
                      <li>• Colostrum de qualité administré rapidement</li>
                      <li>• Alimentation lactée adéquate</li>
                      <li>• Environnement propre et confortable</li>
                      <li>• Absence de stress</li>
                      <li>• Bon état de santé général</li>
                    </ul>
                  </div>
                  <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-700 dark:bg-red-900/20">
                    <h4 className="mb-2 font-semibold text-red-800 dark:text-red-300">Facteurs négatifs</h4>
                    <ul className="space-y-1 text-sm text-red-700 dark:text-red-300">
                      <li>• Maladies (diarrhée, pneumonie)</li>
                      <li>• Alimentation insuffisante</li>
                      <li>• Stress environnemental</li>
                      <li>• Parasites internes</li>
                      <li>• Conditions d'élevage inadéquates</li>
                    </ul>
                  </div>
                </div>

                <h3 className="mb-3 text-xl font-semibold text-stone-800 dark:text-slate-200">
                  Utilisation du GMQ en élevage
                </h3>
                <ul className="mb-4 list-disc space-y-2 pl-6 dark:text-slate-300">
                  <li><strong>Détection précoce :</strong> Identifier rapidement les problèmes de santé</li>
                  <li><strong>Optimisation alimentaire :</strong> Ajuster les rations selon les performances</li>
                  <li><strong>Comparaison :</strong> Évaluer les performances entre veaux</li>
                  <li><strong>Planification :</strong> Prévoir l'âge de sevrage et les objectifs de poids</li>
                  <li><strong>Économie :</strong> Optimiser les coûts d'alimentation</li>
                </ul>

                <div className="mb-6 border-l-4 border-yellow-400 bg-yellow-50 p-4 dark:border-yellow-500 dark:bg-yellow-900/20">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-yellow-400 dark:text-yellow-300"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700 dark:text-yellow-300">
                        <strong>Conseil pratique :</strong> Pesez vos veaux régulièrement 
                        (toutes les 2-4 semaines) pour calculer un GMQ précis. Un GMQ 
                        insuffisant peut indiquer un problème de santé nécessitant 
                        une intervention rapide.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Milk Feeding Section */}
            <div id="milk-feeding" className="mb-12">
              <h2 className="mb-4 text-2xl font-bold text-stone-900 dark:text-slate-100">
                5. Alimentation lactée
              </h2>
              <div className="prose prose-stone max-w-none dark:prose-invert">
                <h3 className="mb-3 text-xl font-semibold text-stone-800 dark:text-slate-200">
                  Principes de l'alimentation lactée
                </h3>
                <p className="mb-4 dark:text-slate-300">
                  L'alimentation lactée est cruciale pour la croissance et la santé 
                  du veau. Elle doit être adaptée à l'âge, au poids et aux besoins 
                  nutritionnels de l'animal. Deux approches principales existent : 
                  l'alimentation contrôlée et l'alimentation ad libitum.
                </p>

                <div className="mb-6 grid gap-4 md:grid-cols-2">
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-700 dark:bg-blue-900/20">
                    <h4 className="mb-2 font-semibold text-blue-800 dark:text-blue-300">Alimentation contrôlée</h4>
                    <ul className="space-y-1 text-sm text-blue-700 dark:text-blue-300">
                      <li>• Quantité fixe par repas</li>
                      <li>• Horaires réguliers</li>
                      <li>• Contrôle précis des apports</li>
                      <li>• Économie de lait</li>
                      <li>• Stimulation de l'appétit pour les concentrés</li>
                    </ul>
                  </div>
                  <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-700 dark:bg-green-900/20">
                    <h4 className="mb-2 font-semibold text-green-800 dark:text-green-300">Alimentation ad libitum</h4>
                    <ul className="space-y-1 text-sm text-green-700 dark:text-green-300">
                      <li>• Lait disponible en permanence</li>
                      <li>• Consommation libre</li>
                      <li>• Croissance optimale</li>
                      <li>• Moins de stress alimentaire</li>
                      <li>• Consommation plus élevée</li>
                    </ul>
                  </div>
                </div>

                <h3 className="mb-3 text-xl font-semibold text-stone-800 dark:text-slate-200">
                  Quantités recommandées
                </h3>
                <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-900/20">
                  <h4 className="mb-2 font-semibold text-amber-800 dark:text-amber-300">
                    Rations journalières selon l'âge :
                  </h4>
                    <ul className="space-y-2 text-sm dark:text-slate-300">
                    <li>
                      <strong>0-1 semaine :</strong> 4-6 litres/jour (colostrum puis lait)
                    </li>
                    <li>
                      <strong>1-2 semaines :</strong> 6-8 litres/jour
                    </li>
                    <li>
                      <strong>2-4 semaines :</strong> 8-10 litres/jour
                    </li>
                    <li>
                      <strong>4-8 semaines :</strong> 8-12 litres/jour
                    </li>
                    <li>
                      <strong>8-12 semaines :</strong> 6-8 litres/jour (sevrage progressif)
                    </li>
                  </ul>
                </div>

                <h3 className="mb-3 text-xl font-semibold text-stone-800 dark:text-slate-200">
                  Types de lait
                </h3>
                <ul className="mb-4 list-disc space-y-2 pl-6 dark:text-slate-300">
                  <li>
                    <strong>Lait entier :</strong> Lait de vache frais, riche en matières grasses
                  </li>
                  <li>
                    <strong>Lait reconstitué :</strong> Poudre de lait reconstituée avec de l'eau
                  </li>
                  <li>
                    <strong>Lait écrémé :</strong> Lait sans matières grasses, nécessite des compléments
                  </li>
                  <li>
                    <strong>Lait de remplacement :</strong> Préparation spécialement formulée pour les veaux
                  </li>
                </ul>

                <h3 className="mb-3 text-xl font-semibold text-stone-800 dark:text-slate-200">
                  Facteurs de qualité
                </h3>
                <div className="mb-6 grid gap-4 md:grid-cols-2">
                  <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-700 dark:bg-green-900/20">
                    <h4 className="mb-2 font-semibold text-green-800 dark:text-green-300">Qualité du lait</h4>
                    <ul className="space-y-1 text-sm text-green-700 dark:text-green-300">
                      <li>• Température appropriée (38-40°C)</li>
                      <li>• Fraîcheur (pas de lait tourné)</li>
                      <li>• Absence de contaminants</li>
                      <li>• Concentration correcte</li>
                      <li>• Hygiène des équipements</li>
                    </ul>
                  </div>
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-700 dark:bg-blue-900/20">
                    <h4 className="mb-2 font-semibold text-blue-800 dark:text-blue-300">Conditions d'élevage</h4>
                    <ul className="space-y-1 text-sm text-blue-700 dark:text-blue-300">
                      <li>• Environnement propre</li>
                      <li>• Abreuvement adéquat</li>
                      <li>• Accès aux concentrés</li>
                      <li>• Logement confortable</li>
                      <li>• Absence de stress</li>
                    </ul>
                  </div>
                </div>

                <div className="mb-6 border-l-4 border-yellow-400 bg-yellow-50 p-4 dark:border-yellow-500 dark:bg-yellow-900/20">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-yellow-400 dark:text-yellow-300"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700 dark:text-yellow-300">
                        <strong>Important :</strong> L'alimentation ad libitum favorise 
                        une croissance optimale mais nécessite une surveillance accrue 
                        pour éviter les troubles digestifs. L'alimentation contrôlée 
                        permet un meilleur contrôle des coûts et facilite le sevrage.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Health Management Section */}
            <div id="health-management" className="mb-12">
              <h2 className="mb-4 text-2xl font-bold text-stone-900 dark:text-slate-100">
                6. Gestion sanitaire
              </h2>
              <div className="prose prose-stone max-w-none dark:prose-invert">
                <h3 className="mb-3 text-xl font-semibold text-stone-800 dark:text-slate-200">
                  Prévention des maladies
                </h3>
                <p className="mb-4 dark:text-slate-300">
                  La prévention est la clé d'un élevage sain. Une bonne gestion 
                  sanitaire commence par la prévention des maladies courantes 
                  chez les veaux.
                </p>

                <div className="mb-6 grid gap-4 md:grid-cols-2">
                  <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-700 dark:bg-green-900/20">
                    <h4 className="mb-2 font-semibold text-green-800 dark:text-green-300">Mesures préventives</h4>
                    <ul className="space-y-1 text-sm text-green-700 dark:text-green-300">
                      <li>• Colostrum de qualité administré rapidement</li>
                      <li>• Environnement propre et sec</li>
                      <li>• Vaccination appropriée</li>
                      <li>• Alimentation équilibrée</li>
                      <li>• Isolement des animaux malades</li>
                    </ul>
                  </div>
                  <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-700 dark:bg-red-900/20">
                    <h4 className="mb-2 font-semibold text-red-800 dark:text-red-300">Maladies courantes</h4>
                    <ul className="space-y-1 text-sm text-red-700 dark:text-red-300">
                      <li>• Diarrhée néonatale</li>
                      <li>• Pneumonie</li>
                      <li>• Omphalite (infection du nombril)</li>
                      <li>• Coccidiose</li>
                      <li>• Parasites internes</li>
                    </ul>
                  </div>
                </div>

                <h3 className="mb-3 text-xl font-semibold text-stone-800 dark:text-slate-200">
                  Signes d'alerte
                </h3>
                <div className="mb-6 rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-700 dark:bg-yellow-900/20">
                  <h4 className="mb-2 font-semibold text-yellow-800 dark:text-yellow-300">
                    Symptômes nécessitant une attention immédiate :
                  </h4>
                    <ul className="space-y-2 text-sm dark:text-yellow-300">
                    <li>
                      <strong>Comportement :</strong> Apathie, refus de manger, isolement
                    </li>
                    <li>
                      <strong>Digestif :</strong> Diarrhée, ballonnement, constipation
                    </li>
                    <li>
                      <strong>Respiratoire :</strong> Toux, écoulement nasal, difficultés respiratoires
                    </li>
                    <li>
                      <strong>Général :</strong> Fièvre, perte de poids, déshydratation
                    </li>
                  </ul>
                </div>

                <h3 className="mb-3 text-xl font-semibold text-stone-800 dark:text-slate-200">
                  Vaccination
                </h3>
                <p className="mb-4 dark:text-slate-300">
                  La vaccination est un élément essentiel de la prévention. 
                  Elle doit être adaptée aux risques spécifiques de l'élevage 
                  et suivre un calendrier approprié.
                </p>

                <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-700 dark:bg-blue-900/20">
                  <h4 className="mb-2 font-semibold text-blue-800 dark:text-blue-300">
                    Calendrier vaccinal type :
                  </h4>
                    <ul className="space-y-2 text-sm dark:text-slate-300">
                    <li>
                      <strong>À la naissance :</strong> Colostrum (immunité passive)
                    </li>
                    <li>
                      <strong>2-4 semaines :</strong> Vaccin contre les maladies respiratoires
                    </li>
                    <li>
                      <strong>6-8 semaines :</strong> Rappel vaccinal
                    </li>
                    <li>
                      <strong>3-4 mois :</strong> Vaccin contre les maladies digestives
                    </li>
                    <li>
                      <strong>6 mois :</strong> Vaccin contre la rage (si nécessaire)
                    </li>
                  </ul>
                </div>

                <div className="mb-6 border-l-4 border-yellow-400 bg-yellow-50 p-4 dark:border-yellow-500 dark:bg-yellow-900/20">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-yellow-400 dark:text-yellow-300"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700 dark:text-yellow-300">
                        <strong>Important :</strong> Consultez toujours un vétérinaire 
                        pour établir un plan de vaccination adapté à votre élevage. 
                        La prévention coûte moins cher que le traitement des maladies.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Procedures Section */}
            <div id="procedures" className="mb-12">
              <h2 className="mb-4 text-2xl font-bold text-stone-900 dark:text-slate-100">
                7. Interventions zootechniques
              </h2>
              <div className="prose prose-stone max-w-none dark:prose-invert">
                <h3 className="mb-3 text-xl font-semibold text-stone-800 dark:text-slate-200">
                  Castration
                </h3>
                <p className="mb-4 dark:text-slate-300">
                  La castration des mâles est une pratique courante en élevage 
                  pour améliorer la docilité, la qualité de la viande et éviter 
                  les comportements agressifs.
                </p>

                <div className="mb-6 grid gap-4 md:grid-cols-2">
                  <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-700 dark:bg-green-900/20">
                    <h4 className="mb-2 font-semibold text-green-800 dark:text-green-300">Avantages</h4>
                    <ul className="space-y-1 text-sm text-green-700 dark:text-green-300">
                      <li>• Amélioration de la docilité</li>
                      <li>• Meilleure qualité de viande</li>
                      <li>• Réduction des comportements agressifs</li>
                      <li>• Facilité de gestion en groupe</li>
                    </ul>
                  </div>
                  <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-700 dark:bg-red-900/20">
                    <h4 className="mb-2 font-semibold text-red-800 dark:text-red-300">Méthodes</h4>
                    <ul className="space-y-1 text-sm text-red-700 dark:text-red-300">
                      <li>• Castration chirurgicale (vétérinaire)</li>
                      <li>• Élastration (anneaux élastiques)</li>
                      <li>• Écrasement des canaux déférents</li>
                      <li>• Castration chimique</li>
                    </ul>
                  </div>
                </div>

                <h3 className="mb-3 text-xl font-semibold text-stone-800 dark:text-slate-200">
                  Écornage
                </h3>
                <p className="mb-4 dark:text-slate-300">
                  L'écornage consiste à retirer ou détruire les bourgeons de cornes 
                  pour éviter les blessures entre animaux et faciliter la manipulation.
                </p>

                <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-700 dark:bg-blue-900/20 p-4">
                  <h4 className="mb-2 font-semibold text-blue-800 dark:text-blue-300">
                    Méthodes d'écornage :
                  </h4>
                    <ul className="space-y-2 text-sm dark:text-slate-300">
                    <li>
                      <strong>Écornage thermique :</strong> Brûlage des bourgeons avec un fer chaud
                    </li>
                    <li>
                      <strong>Écornage chimique :</strong> Application de pâte caustique
                    </li>
                    <li>
                      <strong>Écornage chirurgical :</strong> Ablation chirurgicale (vétérinaire)
                    </li>
                    <li>
                      <strong>Âge optimal :</strong> 1-8 semaines (plus efficace et moins douloureux)
                    </li>
                  </ul>
                </div>

                <h3 className="mb-3 text-xl font-semibold text-stone-800 dark:text-slate-200">
                  Soins post-intervention
                </h3>
                <ul className="mb-4 list-disc space-y-2 pl-6 dark:text-slate-300">
                  <li><strong>Surveillance :</strong> Vérifier l'absence d'infection</li>
                  <li><strong>Antiseptique :</strong> Appliquer un désinfectant si nécessaire</li>
                  <li><strong>Antidouleur :</strong> Administrer des analgésiques selon les cas</li>
                  <li><strong>Isolement :</strong> Séparer temporairement si nécessaire</li>
                  <li><strong>Documentation :</strong> Enregistrer la date et la méthode utilisée</li>
                </ul>

                <div className="mb-6 border-l-4 border-red-400 bg-red-50 p-4 dark:border-red-500 dark:bg-red-900/20">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-red-400 dark:text-red-300"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700 dark:text-red-300">
                        <strong>Important :</strong> Ces interventions doivent être 
                        réalisées par du personnel qualifié ou un vétérinaire. 
                        Respectez la réglementation en vigueur concernant le bien-être 
                        animal et l'utilisation d'antalgiques.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Supplements Section */}
            <div id="supplements" className="mb-12">
              <h2 className="mb-4 text-2xl font-bold text-stone-900 dark:text-slate-100">
                8. Compléments nutritionnels
              </h2>
              <div className="prose prose-stone max-w-none dark:prose-invert">
                <h3 className="mb-3 text-xl font-semibold text-stone-800 dark:text-slate-200">
                  Locatim
                </h3>
                <p className="mb-4 dark:text-slate-300">
                  Le Locatim est un complément nutritionnel spécialement formulé 
                  pour les veaux nouveau-nés. Il contient des nutriments essentiels 
                  qui peuvent être ajoutés au colostrum ou au lait pour améliorer 
                  la santé et la croissance du veau.
                </p>

                <div className="mb-6 border-l-4 border-blue-400 bg-blue-50 p-4 dark:border-blue-500 dark:bg-blue-900/20">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-blue-400 dark:text-blue-300"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        <strong>Composition :</strong> Le Locatim contient généralement 
                        des vitamines, des minéraux, des acides aminés et d'autres 
                        nutriments essentiels pour soutenir le développement du veau 
                        dans ses premiers jours de vie.
                      </p>
                    </div>
                  </div>
                </div>

                <h3 className="mb-3 text-xl font-semibold text-stone-800 dark:text-slate-200">
                  Fer
                </h3>
                <p className="mb-4 dark:text-slate-300">
                  Le fer est un minéral essentiel pour la formation des globules rouges 
                  et le transport de l'oxygène dans l'organisme. Les veaux nouveau-nés 
                  peuvent présenter des carences en fer, particulièrement en élevage 
                  intensif.
                </p>

                <div className="mb-6 grid gap-4 md:grid-cols-2">
                  <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-700 dark:bg-green-900/20">
                    <h4 className="mb-2 font-semibold text-green-800 dark:text-green-300">Bénéfices du fer</h4>
                    <ul className="space-y-1 text-sm text-green-700 dark:text-green-300">
                      <li>• Prévention de l'anémie</li>
                      <li>• Amélioration de la croissance</li>
                      <li>• Renforcement de l'immunité</li>
                      <li>• Meilleure résistance aux maladies</li>
                    </ul>
                  </div>
                  <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-700 dark:bg-yellow-900/20">
                    <h4 className="mb-2 font-semibold text-yellow-800 dark:text-yellow-300">Signes de carence</h4>
                    <ul className="space-y-1 text-sm text-yellow-700 dark:text-yellow-300">
                      <li>• Pâleur des muqueuses</li>
                      <li>• Faiblesse générale</li>
                      <li>• Croissance ralentie</li>
                      <li>• Sensibilité aux infections</li>
                    </ul>
                  </div>
                </div>

                <h3 className="mb-3 text-xl font-semibold text-stone-800 dark:text-slate-200">
                  Sélénium
                </h3>
                <p className="mb-4 dark:text-slate-300">
                  Le sélénium est un oligo-élément essentiel qui agit comme antioxydant 
                  et participe au bon fonctionnement du système immunitaire. Il est 
                  particulièrement important pour les veaux dans les régions où les 
                  sols sont pauvres en sélénium.
                </p>

                <div className="mb-6 rounded-lg border border-purple-200 bg-purple-50 p-4 dark:border-purple-700 dark:bg-purple-900/20">
                  <h4 className="mb-2 font-semibold text-purple-800 dark:text-purple-300">
                    Rôles du sélénium :
                  </h4>
                    <ul className="space-y-2 text-sm dark:text-purple-300">
                    <li>
                      <strong>Antioxydant :</strong> Protège les cellules contre les radicaux libres
                    </li>
                    <li>
                      <strong>Immunité :</strong> Renforce le système immunitaire
                    </li>
                    <li>
                      <strong>Croissance :</strong> Favorise le développement normal
                    </li>
                    <li>
                      <strong>Reproduction :</strong> Important pour la fertilité future
                    </li>
                  </ul>
                </div>

                <h3 className="mb-3 text-xl font-semibold text-stone-800 dark:text-slate-200">
                  Administration des compléments
                </h3>
                <ul className="mb-4 list-disc space-y-2 pl-6 dark:text-slate-300">
                  <li><strong>Mélange au colostrum :</strong> Ajouter au premier colostrum</li>
                  <li><strong>Mélange au lait :</strong> Incorporer dans le lait de remplacement</li>
                  <li><strong>Administration directe :</strong> Par voie orale avec une seringue</li>
                  <li><strong>Injection :</strong> Par un vétérinaire pour certains compléments</li>
                </ul>

                <div className="mb-6 border-l-4 border-yellow-400 bg-yellow-50 p-4 dark:border-yellow-500 dark:bg-yellow-900/20">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-yellow-400 dark:text-yellow-300"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700 dark:text-yellow-300">
                        <strong>Important :</strong> Respectez toujours les dosages 
                        recommandés par le fabricant. Un surdosage peut être dangereux. 
                        Consultez un vétérinaire pour déterminer les besoins spécifiques 
                        de votre élevage.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Conclusion */}
            <div className="mb-12">
              <h2 className="mb-4 text-2xl font-bold text-stone-900 dark:text-slate-100">
                Conclusion
              </h2>
              <div className="prose prose-stone max-w-none dark:prose-invert">
                <p className="mb-4 dark:text-slate-300">
                  Ce guide éducatif vous a présenté les concepts fondamentaux de 
                  l'élevage de veaux. Comprendre ces notions essentielles - du BDTA 
                  au GMQ, en passant par l'importance du colostrum et la gestion 
                  sanitaire - vous permettra de mieux appréhender les défis de 
                  l'élevage bovin moderne.
                </p>

                <div className="mb-6 border-l-4 border-green-400 bg-green-50 p-4 dark:border-green-500 dark:bg-green-900/20">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-green-400 dark:text-green-300"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-green-700 dark:text-green-300">
                        <strong>Points clés à retenir :</strong> La réussite en élevage 
                        repose sur la prévention, l'observation attentive et l'application 
                        rigoureuse des bonnes pratiques. Chaque paramètre que vous 
                        enregistrez dans l'application a une signification importante 
                        pour la santé et la croissance de vos veaux.
                      </p>
                    </div>
                  </div>
                </div>

                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-900/20">
                    <h4 className="mb-2 font-semibold text-amber-800 dark:text-amber-300">
                    Ressources supplémentaires :
                  </h4>
                    <ul className="space-y-2 text-sm dark:text-slate-300">
                    <li>
                      •{" "}
                      <a
                        href="https://www.agroscope.admin.ch"
                        className="text-amber-700 underline hover:text-amber-900"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Agroscope - Centre de compétences de la Confédération
                        pour la recherche agricole
                      </a>
                    </li>
                    <li>
                      •{" "}
                      <a
                        href="https://www.blv.admin.ch"
                        className="text-amber-700 underline hover:text-amber-900"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Office fédéral de la sécurité alimentaire et des
                        affaires vétérinaires (OSAV)
                      </a>
                    </li>
                    <li>
                      •{" "}
                      <a
                        href="https://www.grangeneuve.ch"
                        className="text-amber-700 underline hover:text-amber-900"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Grangeneuve - Institut agricole de l'État de Fribourg
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Contact Section */}
            <div className="mb-8 rounded-lg border border-stone-200 bg-stone-50 p-6 dark:border-slate-700 dark:bg-slate-800">
              <h3 className="mb-3 text-lg font-semibold text-stone-800 dark:text-slate-200">
                Besoin d'aide ?
              </h3>
              <p className="mb-3 text-sm text-stone-600 dark:text-slate-300">
                Si vous avez des questions ou besoin d'assistance
                supplémentaire, n'hésitez pas à contacter votre instructeur.
              </p>
              <p className="text-xs text-stone-500 dark:text-slate-400">
                Application développée par Grangeneuve pour l'éducation
                agricole.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Help;
